import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Icon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { UserPlus } from "lucide-react";
import { useState } from "react";
import {
  DeleteUserDialog,
  UserForm,
  UserList,
} from "../../components/features/settings";
import { useUserManagement } from "../../hooks/useUserManagement";

export function SettingsPage() {
  const {
    users,
    loading,
    isAdmin,
    currentUserEmail,
    sendingVerification,
    createUser,
    updateUserRole,
    deleteUser,
    handleSendVerificationEmail,
  } = useUserManagement();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  const handleCreateUser = async (userData: {
    email: string;
    password: string;
    name: string;
  }) => {
    setCreateUserLoading(true);
    try {
      await createUser(userData);
      setIsCreateModalOpen(false);
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteUserLoading(true);
    try {
      await deleteUser(userToDelete);
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    } finally {
      setDeleteUserLoading(false);
    }
  };

  const confirmDelete = (userId: string) => {
    setUserToDelete(userId);
    setIsDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <Center p={8} minH="200px">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Center>
    );
  }

  if (!isAdmin) {
    return (
      <Center p={8} minH="200px">
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          borderRadius="lg"
          p={6}
          maxW="md"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Acesso Restrito
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            Você não tem permissão para gerenciar usuários. Esta seção é apenas
            para administradores.
          </AlertDescription>
        </Alert>
      </Center>
    );
  }

  return (
    <Box p={{ base: 2, md: 6 }}>
      <HStack justify="space-between" mb={6}>
        <Box>
          <Heading size="lg" mb={2}>
            Configurações de Usuários
          </Heading>
          <Text color="gray.600">
            Gerencie os usuários do sistema e suas permissões.
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={UserPlus} />}
          colorScheme="blue"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Novo Usuário
        </Button>
      </HStack>

      <UserList
        users={users}
        currentUserEmail={currentUserEmail}
        sendingVerification={sendingVerification}
        onRoleChange={updateUserRole}
        onSendVerification={handleSendVerificationEmail}
        onDelete={confirmDelete}
      />

      <UserForm
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        isLoading={createUserLoading}
      />

      <DeleteUserDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteUser}
        isLoading={deleteUserLoading}
      />
    </Box>
  );
}
